'use client'

import { X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { CATALOGUE_SEARCH_ID } from '@/components/sections/order/order-catalogue.client'
import { OrderLineCard } from '@/components/sections/order/order-line-card.client'
import type { OrderLinesApi } from '@/components/sections/order/use-order-lines.client'
import type { CreatorCityView } from '@/lib/creator'
import type { LineView } from '@/lib/order-line-view'
import { BODY, META, MICRO } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * The order's positions — the ONE list.
 *
 * There is no "Dodaj do zamówienia" button any more, and its absence is the
 * whole point: a line is in the order from the moment it exists, and an
 * unfinished one says what it needs instead of being held outside by a disabled
 * control. The gate moved from the button to the field.
 *
 * ONLY LINES LIVE HERE. This list also used to render the featured offerings
 * and whatever a deep link had named, in the same frame and the same column —
 * so an empty basket drew three cards nobody had chosen and the screen read as
 * an order somebody else had started. The catalogue's own panel followed it out
 * of this file for the same reason; both now live in `OrderCatalogue`, under
 * their own heading. The rule this list keeps is that everything inside it is
 * in the order.
 *
 * ── ONE POSITION IS OPEN AT A TIME ───────────────────────────────────────
 * Not a preference — the alternative measured 5.3 screens for five positions,
 * with no view at any zoom level of what the order contained. An accordion is
 * what turns the column back into a list you can read: closed cards carry their
 * own answers and their own price, and opening the next one puts the form where
 * your eye already is instead of three screens down.
 *
 * Which one opens is decided rather than remembered: the first position that is
 * still missing something, until the reader says otherwise. Adding a position
 * opens it, because a position nobody has configured is the one they just asked
 * for.
 *
 * One `aria-live` region for the whole screen, and it deliberately does NOT
 * carry the amount: that changes on every press of a stepper, and a region that
 * reads a new number twice a second is a screen reader nobody can use. What it
 * announces is what the SERVER did — a refusal, or two lines becoming one.
 */
export function OrderLineList({
  views,
  cities,
  films,
  order,
}: {
  views: LineView[]
  cities: CreatorCityView[]
  /** The films in this order, for "rolki z tego filmu" (spec §8 · G8). */
  films: { lineId: string; label: string }[]
  order: OrderLinesApi
}) {
  const listRef = useRef<HTMLDivElement>(null)
  /** Which card was just removed, so focus can land on its neighbour. */
  const removed = useRef<number | null>(null)

  /**
   * The reader's own choice, once they have made one.
   *
   * Three states, and the empty string is the one worth naming: `null` is "has
   * not chosen", `''` is "chose to close everything". Collapsing them would
   * mean a card the reader deliberately shut springing back open.
   */
  const [chosen, setChosen] = useState<string | null>(null)

  const firstOpen = views.find((view) => view.status === 'draft')?.line.lineId ?? null
  const alive = chosen !== null && views.some((view) => view.line.lineId === chosen)
  const openId = alive ? chosen : chosen === '' ? '' : firstOpen

  // A position that has just been added is one nobody has filled in — open it,
  // wherever it landed in the list. Keyed on the COUNT: the ids change on every
  // server answer, and comparing those would re-open a card on each round trip.
  const lastId = views.at(-1)?.line.lineId ?? null
  const lastIdRef = useRef(lastId)
  lastIdRef.current = lastId
  const countRef = useRef(views.length)
  useEffect(() => {
    if (views.length > countRef.current && lastIdRef.current) setChosen(lastIdRef.current)
    countRef.current = views.length
  }, [views.length])

  useEffect(() => {
    const index = removed.current
    if (index === null) return
    removed.current = null
    // Removing the focused control leaves focus on `<body>`, which drops a
    // keyboard user back to the top of the document — several tab stops away
    // from the list they were working in.
    const buttons = listRef.current?.querySelectorAll<HTMLButtonElement>('[data-line-remove]')
    if (buttons && buttons.length > 0) {
      buttons[Math.min(index, buttons.length - 1)]?.focus()
      return
    }
    document.getElementById(CATALOGUE_SEARCH_ID)?.focus()
  }, [views])

  const ready = views.filter((view) => view.status === 'ready').length

  return (
    <div
      id="pozycje"
      ref={listRef}
      aria-busy={order.pending}
      className="flex scroll-mt-28 flex-col gap-3"
    >
      <p role="status" aria-live="polite" className="sr-only">
        {order.notice ?? ''}
      </p>

      {/* What the list is and how far along it is. The screen could say how much
          the order cost and could not say how much of it was finished — which
          is the question somebody halfway through is actually asking. */}
      {views.length > 0 ? (
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 pb-1">
          <h2 className="text-[1.05rem] font-bold text-white">Twoje pozycje</h2>
          <p className={cn(META, 'text-white/55')}>
            {ready === views.length ? (
              <span className="font-semibold text-[oklch(0.82_0.17_150)]">
                Wszystko gotowe — zostały dane
              </span>
            ) : (
              <>
                <span className="font-semibold text-white/80">
                  {ready} z {views.length}
                </span>{' '}
                gotowe
              </>
            )}
          </p>
        </div>
      ) : null}

      {order.notice ? (
        <div
          className={cn(
            META,
            'flex items-start justify-between gap-3 rounded-2xl border border-[oklch(0.8_0.15_60_/_0.35)] bg-[oklch(0.8_0.15_60_/_0.1)] px-4 py-3 text-white/80',
          )}
        >
          <span>{order.notice}</span>
          <button
            type="button"
            onClick={order.dismiss}
            aria-label="Zamknij komunikat"
            className="grid min-h-11 min-w-11 shrink-0 place-items-center rounded-full text-white/55 transition hover:bg-white/10 hover:text-white"
          >
            <X size={13} aria-hidden="true" />
          </button>
        </div>
      ) : null}

      {views.map((view, index) =>
        view.status === 'orphan' ? (
          // No button. The line is already on its way out — the server sweeps
          // these before the page paints, and `OrderPageView` drops whatever
          // slips through while the tab is open. What is left to do here is say
          // so, for the half-second it is still on screen; asking the customer
          // to press "Usuń pozycję" was asking them to tidy up after a decision
          // somebody else made.
          <section
            key={view.line.lineId}
            aria-label="Pozycja niedostępna"
            className="flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-[oklch(0.8_0.15_60_/_0.3)] bg-[oklch(0.8_0.15_60_/_0.06)] p-6"
          >
            <p className={cn(BODY, 'text-white/70')}>{view.problem}</p>
          </section>
        ) : (
          <OrderLineCard
            key={view.line.lineId}
            view={view}
            cities={cities}
            open={openId === view.line.lineId}
            onToggle={() => setChosen(openId === view.line.lineId ? '' : view.line.lineId)}
            // A line is never offered as its own source. `dependencyProblem`
            // refuses that too, but a control that shows an impossible choice
            // is a control somebody will click.
            films={films.filter((film) => film.lineId !== view.line.lineId)}
            onPatch={(next) => order.patch(view.line.lineId, next)}
            onDrop={() => {
              removed.current = index
              order.drop(view.line.lineId)
            }}
          />
        ),
      )}

      {/* The only erasure route that actually reaches somebody with no account
          and no address on file, whose one identifier is the cookie they are
          asking us to forget. Retention would get there in thirty days; a person
          asking should not have to wait for it. */}
      {order.stored ? (
        <p className={cn(MICRO, 'text-white/55')}>
          Trzymamy ten koszyk na naszym serwerze przez 30 dni, żebyś zastał go tak samo na innym
          urządzeniu.{' '}
          <button
            type="button"
            onClick={order.discard}
            className="font-semibold text-white/60 underline underline-offset-4 transition hover:text-white"
          >
            Usuń zapisaną kopię
          </button>
        </p>
      ) : null}
    </div>
  )
}
